import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import ProjectCard from "@/components/common/ProjectCard";
import { Breadcrumbs, EmptyState, ErrorState, GridSkeleton, inputCls } from "@/components/common/states";
import { REGIONS, DEVELOPERS, PROJECT_STATUS } from "@/lib/constants";
import { track, Events } from "@/lib/analytics";

const SORTS = [
  { value: "recommended", label: "Recommended" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "newest", label: "Newest" },
];

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const filters = {
    region: params.get("region") || "",
    developer: params.get("developer") || "",
    status: params.get("status") || "",
    search: params.get("search") || "",
    sort: params.get("sort") || "recommended",
  };

  useSEO({ title: "Projects in Pune", description: "Explore 40+ residential projects across West, East, North, South and Prime Pune from leading developers — with verified details and enquiry.", path: "/projects" });

  const load = useCallback(() => {
    setData(null); setError(false);
    const q = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) q[k] = v; });
    apiGet("/projects", q).then((d) => setData(d.items)).catch(() => setError(true));
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
    if (key !== "sort") track(Events.FILTER_USED, { key, value });
  };
  const clear = () => setParams(new URLSearchParams());

  return (
    <div className="ec-container py-8 sm:py-12">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Projects" }]} />
      <div className="mt-4 mb-6">
        <h1 className="font-display text-3xl sm:text-4xl text-foreground">Projects in Pune</h1>
        <p className="text-muted-foreground mt-2">Curated residential projects across Pune's key corridors, organised by region.</p>
      </div>

      {/* Region pills */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button data-testid="region-all" onClick={() => update("region", "")}
          className={`px-3.5 py-1.5 rounded-sm text-sm font-medium transition-colors ${!filters.region ? "bg-primary text-primary-foreground" : "bg-white border border-border hover:border-primary"}`}>All Pune</button>
        {REGIONS.map((r) => (
          <button key={r.value} data-testid={`region-${r.value.toLowerCase()}`} onClick={() => update("region", r.value)}
            className={`px-3.5 py-1.5 rounded-sm text-sm font-medium transition-colors ${filters.region === r.value ? "bg-primary text-primary-foreground" : "bg-white border border-border hover:border-primary"}`}>{r.label}</button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-8">
        <select data-testid="filter-developer" value={filters.developer} onChange={(e) => update("developer", e.target.value)} className={`${inputCls} h-10 w-auto`}>
          <option value="">All Developers</option>
          {DEVELOPERS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select data-testid="filter-status" value={filters.status} onChange={(e) => update("status", e.target.value)} className={`${inputCls} h-10 w-auto`}>
          <option value="">Any Status</option>
          {PROJECT_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <input data-testid="project-search" value={filters.search} onChange={(e) => update("search", e.target.value)} placeholder="Search project, area, developer" className={`${inputCls} h-10 w-64`} />
        <select data-testid="sort-select" value={filters.sort} onChange={(e) => update("sort", e.target.value)} className={`${inputCls} h-10 w-auto ml-auto`}>
          {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {error ? <ErrorState onRetry={load} /> : data === null ? <GridSkeleton /> :
        data.length === 0 ? (
          <EmptyState title="No projects match your filters" message="Try a different region or clear the filters."
            action={<button onClick={clear} className="h-11 px-6 rounded-md bg-primary text-primary-foreground text-sm font-semibold">Clear filters</button>} />
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">{data.length} projects</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.map((p, i) => <ProjectCard key={p.slug} project={p} index={i} />)}
            </div>
          </>
        )}
    </div>
  );
}
